// Updated: Added optional location, timeslot and note fields to event interface so they flow through to the EventModal
import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { Calendar, Trash2, Image as ImageIcon, Pencil as Edit, Plus, UserCheck, Link, Check, Banknote, AlertTriangle, Lock, Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import EventModal from './EventModal';
import Collapsible from '../components/Collapsible';
import RichTextEditor from '../../RichTextEditor';
import { useToastContext } from '../../../contexts/ToastContext';
import { validateImageFile, convertToWebp } from '../../../utils/imageUpload';

interface Event {
  id: string;
  date: string;
  description: string;
  joinable?: boolean;
  is_new?: boolean;
  tikkie_url?: string | null;
  tikkie_price_label?: string | null;
  tikkie_url_set_at?: string | null;
  location?: string | null;
  timeslot?: string | null;
  note?: string | null;
}

const EXPIRY_DAYS = 14;

const isTikkieExpired = (setAt: string | null | undefined): boolean => {
  if (!setAt) return false;
  const daysSince = Math.floor((Date.now() - new Date(setAt).getTime()) / (1000 * 60 * 60 * 24));
  return daysSince >= EXPIRY_DAYS;
};

const getTikkieDaysLeft = (setAt: string | null | undefined): number => {
  if (!setAt) return 0;
  const daysSince = Math.floor((Date.now() - new Date(setAt).getTime()) / (1000 * 60 * 60 * 24));
  return EXPIRY_DAYS - daysSince;
};

const isEventPast = (dateString: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const [year, month, day] = dateString.split('-').map(Number);
  const eventDate = new Date(year, month - 1, day);
  return eventDate < today;
};

const EventsSection = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | undefined>();
  const [footerText, setFooterText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editorKey, setEditorKey] = useState<number>(Date.now());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { addToast } = useToastContext();
  const EVENTS_PER_PAGE = 10;

  const sortedAndFilteredEvents = useMemo(() => {
    const filtered = events.filter((event) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        event.description.toLowerCase().includes(query) ||
        formatDate(event.date).toLowerCase().includes(query)
      );
    });

    const active = filtered.filter((e) => !isEventPast(e.date)).sort((a, b) => a.date.localeCompare(b.date));
    const past = filtered.filter((e) => isEventPast(e.date)).sort((a, b) => b.date.localeCompare(a.date));
    return [...active, ...past];
  }, [events, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(sortedAndFilteredEvents.length / EVENTS_PER_PAGE));
  const paginatedEvents = sortedAndFilteredEvents.slice(
    (currentPage - 1) * EVENTS_PER_PAGE,
    currentPage * EVENTS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const copyEventLink = async (eventId: string) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/?event=${eventId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(eventId);
      addToast('Link gekopieerd!', 'success');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      addToast('Kan link niet kopieren', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(Date.UTC(
      parseInt(year),
      parseInt(month) - 1, // Months are 0-based
      parseInt(day)
    ));

    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC' // Force UTC timezone to prevent any conversions
    });
  };

  useEffect(() => {
    fetchEvents();
    fetchSettings();
    fetchFooterText();
  }, []);

  const fetchFooterText = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'events_footer_text')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      const newText = data?.value || '';
      setFooterText(newText);
      setEditorKey(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching footer text:', error);
    }
  };

  const updateFooterText = async () => {
    try {
      // Preserve whitespace by using the raw HTML content
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key: 'events_footer_text',
          value: footerText.replace(/\n/g, '<br />')
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      addToast('Tekst succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating footer text:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de tekst', 'error');
    }
  };

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      return;
    }

    const rawFile = event.target.files[0];
    const validation = validateImageFile(rawFile);
    if (!validation.ok) {
      addToast(validation.message, 'error');
      event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(rawFile);
    setPreviewImage(objectUrl);
    setUploading(true);

    try {
      const file = await convertToWebp(rawFile);

      // Delete old image if exists
      if (imageUrl) {
        const oldFilePath = imageUrl.split('/').pop();
        if (oldFilePath) {
          await supabase.storage
            .from('images')
            .remove([`events/${oldFilePath}`]);
        }
      }

      const fileName = `agenda-${Date.now()}.webp`;
      const filePath = `events/${fileName}`;

      // Upload new image
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // Update settings
      const { error: updateError } = await supabase
        .from('settings')
        .upsert({ 
          key: 'agenda_image',
          value: publicUrl
        }, {
          onConflict: 'key'
        });

      if (updateError) throw updateError;

      setImageUrl(publicUrl);
      addToast('Afbeelding succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error uploading image:', error);
      addToast('Er is een fout opgetreden bij het uploaden van de afbeelding', 'error');
    } finally {
      URL.revokeObjectURL(objectUrl);
      setPreviewImage('');
      setUploading(false);
      event.target.value = '';
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchSettings();
  }, []);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('date', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('value')
        .eq('key', 'agenda_image')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setImageUrl(data?.value || '');
    } catch (error) {
      console.error('Error fetching image URL:', error);
      setImageUrl('');
    }
  };

  const handleEdit = (event: Event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedEvent(undefined);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const updateImage = async () => {
    try {
      const { error } = await supabase
        .from('settings')
        .upsert({ 
          key: 'agenda_image',
          value: imageUrl
        }, {
          onConflict: 'key'
        }, {
          onConflict: 'key'
        });

      if (error) throw error;
      addToast('Afbeelding succesvol bijgewerkt', 'success');
    } catch (error) {
      console.error('Error updating image:', error);
      addToast('Er is een fout opgetreden bij het bijwerken van de afbeelding', 'error');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-6">
          <Collapsible title="Afbeelding aanpassen">
            <div className="flex gap-4 items-end">
              <label className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white ${uploading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`}>
                <input
                  type="file"
                  accept="image/webp,image/png,image/jpeg"
                  onChange={uploadImage}
                  disabled={uploading}
                  className="hidden"
                />
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bezig met uploaden...</>
                ) : (
                  'Kies bestand'
                )}
              </label>
            </div>
            {(previewImage || imageUrl) && (
              <div className="mt-4 relative w-64 h-64">
                <img
                  src={previewImage || imageUrl}
                  alt="Events section"
                  className={`w-64 h-64 object-cover rounded-lg transition-opacity ${uploading ? 'opacity-70' : ''}`}
                />
                {uploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>
            )}
          </Collapsible>
        
        <Collapsible title="Agenda tekst aanpassen">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agenda tekst
              </label>
              <div className="space-y-4 w-full">
                <RichTextEditor
                  key={editorKey}
                  content={footerText}
                  onChange={(value) => {
                    setFooterText(value);
                  }}
                />
                <button
                  onClick={updateFooterText}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                >
                  Tekst aanpassen
                </button>
              </div>
            </div>
          </div>
        </Collapsible>
        
          <Collapsible title="Evenementen beheren">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <button
                  onClick={handleAdd}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#E37C64] hover:bg-[#5A130E]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nieuw evenement toevoegen
                </button>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Zoek evenementen..."
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                  />
                </div>
              </div>

              {sortedAndFilteredEvents.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  {searchQuery ? 'Geen evenementen gevonden.' : 'Nog geen evenementen.'}
                </p>
              ) : (
                <>
                  <div className="space-y-3">
                    {paginatedEvents.map((event) => {
                      const past = isEventPast(event.date);
                      return (
                        <div
                          key={event.id}
                          className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${past ? 'opacity-50 bg-gray-50 border-gray-200' : 'border-gray-200'}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold">
                                {formatDate(event.date)}
                              </p>
                              {past && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                                  <Lock className="w-3 h-3" />
                                  Verlopen
                                </span>
                              )}
                              {!past && event.joinable && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <UserCheck className="w-3 h-3" />
                                  Aanmeldbaar
                                </span>
                              )}
                              {!past && event.tikkie_url && (
                                isTikkieExpired(event.tikkie_url_set_at) ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <AlertTriangle className="w-3 h-3" />
                                    Tikkie verlopen
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                    <Banknote className="w-3 h-3" />
                                    Alternatief Tikkie{event.tikkie_price_label ? ` • ${event.tikkie_price_label}` : ''}{event.tikkie_url_set_at ? ` • Verloopt over ${getTikkieDaysLeft(event.tikkie_url_set_at)} ${getTikkieDaysLeft(event.tikkie_url_set_at) === 1 ? 'dag' : 'dagen'}` : ''}
                                  </span>
                                )
                              )}
                            </div>
                            <p className="truncate">{event.description}</p>
                          </div>
                          <div className="flex gap-2 items-center shrink-0 ml-4">
                            {!past && event.joinable && (
                              <button
                                onClick={() => copyEventLink(event.id)}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                                title="Kopieer aanmeldlink"
                              >
                                {copiedId === event.id ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <Link className="w-4 h-4" />
                                )}
                                <span>{copiedId === event.id ? 'Gekopieerd!' : 'Kopieer link'}</span>
                              </button>
                            )}
                            {!past && (
                              <button
                                onClick={() => handleEdit(event)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(event.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-500">
                        {sortedAndFilteredEvents.length} evenement{sortedAndFilteredEvents.length !== 1 ? 'en' : ''}
                      </p>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="text-xs text-gray-600 px-2 min-w-[4rem] text-center">
                          {currentPage} / {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Collapsible>          
        </div>
      </div>
      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={selectedEvent}
        onSuccess={fetchEvents}
      />
    </div>
  );
};

export default EventsSection;